/**
 * Agente de asignaciones (T3b): el manager escribe "Juan trabaja con Nubank en paid media al 50 %"
 * y el agente deja sugeridas, que se confirman con el botón del panel (nunca desde el chat).
 * Chat con las piezas compartidas (ChatBubble / ChatComposer / useChatScroll); las propuestas
 * viajan como estructura y se dibujan como tarjetas (catálogo agent-chat-attachment).
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';
import { ChatBubble, ThinkingBubble } from '@/components/chat/ChatBubble';
import { ChatComposer } from '@/components/chat/ChatComposer';
import { useChatScroll } from '@/components/chat/useChatScroll';
import { EnumBadge } from '@/components/ui/enum-badge';
import { useOrgAgentAsk } from '@/hooks/useOrg';
import { solutionName } from '@/components/org/org-format';
import type { RouterOutputs } from '@/lib/api-types';
import { normalizeLang } from '../../../../shared/lang';

type AgentReply = RouterOutputs['org']['agent']['ask'];
interface Msg {
  role: 'user' | 'assistant';
  content: string;
  proposals?: AgentReply['proposals'];
  rejected?: AgentReply['rejected'];
  /** Burbuja de error local: no vuelve al modelo como historial. */
  error?: boolean;
}

const isForbidden = (e: unknown) => (e as { data?: { code?: string } } | null)?.data?.code === 'FORBIDDEN';

export function OrgAgentChat() {
  const { t, i18n } = useTranslation();
  const ask = useOrgAgentAsk();
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<Msg[]>([]);
  const endRef = useChatScroll([messages, ask.isPending]);

  const send = () => {
    const message = input.trim();
    if (!message || ask.isPending) return;
    const history = messages.filter((m) => !m.error).slice(-8).map((m) => ({ role: m.role, content: m.content }));
    setMessages((prev) => [...prev, { role: 'user', content: message }]);
    setInput('');
    ask.mutate(
      { message, history, lang: normalizeLang(i18n.language) },
      {
        onSuccess: (r) => setMessages((prev) => [...prev, { role: 'assistant', content: r.reply, proposals: r.proposals, rejected: r.rejected }]),
        onError: (e) => {
          const key = isForbidden(e) ? 'org.agent.forbidden' : 'org.agent.error';
          setMessages((prev) => [...prev, { role: 'assistant', error: true, content: t(key, 'El agente no pudo responder. Probá de nuevo con una frase más simple.') }]);
        },
      },
    );
  };

  return (
    <section className="space-y-3 rounded-xl border p-4">
      <div>
        <h3 className="flex items-center gap-2 text-sm font-semibold"><Sparkles className="h-4 w-4" />{t('org.agent.title', 'Contale al agente quién trabaja con qué cliente')}</h3>
        <p className="text-xs text-muted-foreground">{t('org.agent.help', 'Para lo que la app de Carga de Horas no tiene. Escribí en lenguaje natural ("Juan trabaja con Nubank en Paid Media al 50 %"); el agente lo deja como sugerido y vos lo confirmás con el botón de arriba.')}</p>
      </div>
      <div className="max-h-80 space-y-2 overflow-y-auto" role="log" aria-live="polite">
        {messages.map((m, i) => (
          <div key={i} className="space-y-2">
            <ChatBubble role={m.role}><span className="whitespace-pre-wrap">{m.content}</span></ChatBubble>
            {m.proposals && m.proposals.length > 0 && (
              <ul className="ml-2 space-y-1" aria-label={t('org.agent.proposals', 'Propuestas')}>
                {m.proposals.map((p, j) => (
                  <li key={j} className="flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2 text-sm">
                    <span className="font-medium">{p.personName}</span>
                    <span className="text-muted-foreground" aria-hidden="true">→</span>
                    <span>{p.clientName}</span>
                    <span className="text-muted-foreground">· {solutionName(p.solutionSlug)}{p.pct != null ? ` · ${Math.round(p.pct)} %` : ''}</span>
                    <EnumBadge value={p.result} tone={p.result === 'created' ? 'warning' : p.result === 'exists' ? 'muted' : 'danger'} label={t(`org.agent.result_${p.result}`, p.result)} />
                  </li>
                ))}
              </ul>
            )}
            {m.rejected && m.rejected.length > 0 && (
              <ul className="ml-2 list-disc pl-4 text-xs text-muted-foreground">
                {m.rejected.map((r, j) => <li key={j}>{t(`org.agent.rejected_${r.reason}`, r.reason, { person: r.person, client: r.client })}</li>)}
              </ul>
            )}
          </div>
        ))}
        {ask.isPending && <ThinkingBubble label={t('org.agent.thinking', 'Pensando…')} />}
        <div ref={endRef} />
      </div>
      <ChatComposer value={input} onChange={setInput} onSend={send} pending={ask.isPending} placeholder={t('org.agent.placeholder', 'Ej.: Ana trabaja con LATAM en Content al 30 %')} maxLength={2000} />
    </section>
  );
}
